/**
 * Email Preview Console — admin tool to render any customer-facing
 * email in any supported language without actually sending it.
 *
 * Available to:
 *   - super_admin (Platform Owners)
 *   - admin (LoJack operations)
 * NOT visible to: it, viewer, dealer, seller.
 *
 * UX layout:
 *   - Left column: type + country + customer_type dropdowns
 *   - Right column: live iframe rendering of the email HTML
 *   - "Refresh" button to re-render
 *
 * The backend never sends real emails — only returns the rendered HTML.
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, CheckCheck, CheckCircle2, Circle, Clock, MailOpen, RefreshCw, AlertCircle, Loader2, Grid3x3, Send, Square, FileText, Pencil, RotateCcw, Palette } from "lucide-react";
import { API } from "../config";
import { EmailTemplateEditor } from "../components/admin/EmailTemplateEditor";

// Flag emoji helper — keeps the dropdown legible for non-Italian admins.
const FLAGS = {
  IT: "🇮🇹", UK: "🇬🇧", FR: "🇫🇷", ES: "🇪🇸", DE: "🇩🇪",
  NL: "🇳🇱", PT: "🇵🇹", PL: "🇵🇱", SE: "🇸🇪", NO: "🇳🇴",
  FI: "🇫🇮", DK: "🇩🇰", CZ: "🇨🇿", GR: "🇬🇷", HU: "🇭🇺",
  RO: "🇷🇴", SK: "🇸🇰", SI: "🇸🇮", HR: "🇭🇷", BG: "🇧🇬",
  EE: "🇪🇪", LT: "🇱🇹", LV: "🇱🇻", MT: "🇲🇹", AT: "🇦🇹",
  BE: "🇧🇪", CH: "🇨🇭", GBZ: "🇬🇮", IRL: "🇮🇪", LU: "🇱🇺", CY: "🇨🇾",
};

const EmailPreviewPage = () => {
  const [options, setOptions] = useState({ email_types: [], countries: [] });
  const [emailType, setEmailType] = useState("welcome");
  const [country, setCountry] = useState("IT");
  const [customerType, setCustomerType] = useState("private");
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // QA mode toggle: "single" (default) or "multi" (side-by-side 14 GDPR-native packs)
  const [viewMode, setViewMode] = useState("single");
  const [multiResults, setMultiResults] = useState([]);
  const [approvals, setApprovals] = useState({ items: [], approved_count: 0, total_combinations: 0, approval_pct: 0 });
  const [approvalNotes, setApprovalNotes] = useState("");

  // ── Edit panel (P2 Mara CR 17/06/2026) ──────────────────────────
  // Whitelisted fixture overrides — let QA replay the preview with
  // the actual customer being onboarded, useful for spotting issues
  // with long names, accented chars, oddly-shaped VINs etc.
  const FIXTURE_FIELDS = [
    { key: "first_name", label: "Nome", placeholder: "Maria" },
    { key: "last_name", label: "Cognome", placeholder: "Bianchi" },
    { key: "company_name", label: "Ragione Sociale", placeholder: "Acme S.p.A." },
    { key: "email", label: "Email cliente", placeholder: "preview@example.com" },
    { key: "phone", label: "Telefono", placeholder: "+39 333 1234567" },
    { key: "vehicle_brand", label: "Marca Veicolo", placeholder: "Fiat" },
    { key: "vehicle_model", label: "Modello", placeholder: "500X" },
    { key: "license_plate", label: "Targa", placeholder: "AB123CD" },
    { key: "vin", label: "VIN", placeholder: "ZFA33400006789012" },
  ];
  const [editOpen, setEditOpen] = useState(false);
  const [fixtureOverrides, setFixtureOverrides] = useState({});
  const overridesActive = useMemo(
    () => Object.values(fixtureOverrides).some(v => (v ?? "").trim().length > 0),
    [fixtureOverrides],
  );
  const overridesJson = useMemo(() => {
    const clean = {};
    for (const [k, v] of Object.entries(fixtureOverrides)) {
      if ((v ?? "").trim()) clean[k] = v.trim();
    }
    return Object.keys(clean).length ? JSON.stringify(clean) : "";
  }, [fixtureOverrides]);
  const resetOverrides = useCallback(() => setFixtureOverrides({}), []);

  // Helper: find approval state for current (emailType, country) pair
  const currentApproval = useMemo(() => {
    return approvals.items.find(a => a.email_type === emailType && a.country === country) || null;
  }, [approvals, emailType, country]);

  // Sync notes when the (type, country) pair changes
  useEffect(() => {
    setApprovalNotes(currentApproval?.notes || "");
  }, [currentApproval]);

  const fetchApprovals = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/email-preview/approvals`);
      setApprovals(r.data);
    } catch (_) { /* non-blocking */ }
  }, []);

  useEffect(() => { fetchApprovals(); }, [fetchApprovals]);

  const toggleApproval = useCallback(async (newApproved) => {
    try {
      await axios.patch(
        `${API}/admin/email-preview/approvals/${emailType}/${country}`,
        { approved: newApproved, notes: approvalNotes },
      );
      toast.success(newApproved ? "Email approvata" : "Approvazione revocata");
      await fetchApprovals();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore aggiornamento approvazione");
    }
  }, [emailType, country, approvalNotes, fetchApprovals]);

  // Bulk-by-country: approve every email type for the current country
  // in a single round-trip (QA legal workflow — Zorzutti 29/06/2026).
  const [bulkLoading, setBulkLoading] = useState(false);
  const bulkApproveForCountry = useCallback(async () => {
    const totalTypes = options?.email_types?.length || 22;
    const ok = window.confirm(
      `Stai per approvare in blocco TUTTI i ${totalTypes} template email per ${country}.\n\n` +
      `Verranno marcate come approvate da te (anche quelle non ancora viste singolarmente).\n` +
      `Le note opzionali batch saranno: "${approvalNotes || '(nessuna nota)'}"\n\n` +
      `Continuare?`
    );
    if (!ok) return;
    setBulkLoading(true);
    try {
      const r = await axios.post(
        `${API}/admin/email-preview/approvals/bulk-by-country`,
        {
          country,
          approved: true,
          notes: approvalNotes || `Approvazione bulk per ${country} via QA console`,
        },
      );
      const d = r.data || {};
      toast.success(
        `${country}: ${d.changed_count}/${d.requested_count} approvate · ` +
        `${d.unchanged_count} già a target (totale globale ${d.approval_progress?.approved_count}/${d.approval_progress?.total_combinations})`
      );
      await fetchApprovals();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore approvazione bulk");
    } finally {
      setBulkLoading(false);
    }
  }, [country, approvalNotes, options, fetchApprovals]);

  // Fetch dropdown options on mount.
  useEffect(() => {
    axios.get(`${API}/admin/email-preview/options`)
      .then(r => setOptions(r.data))
      .catch(() => toast.error("Impossibile caricare le opzioni"));
  }, []);

  // Single-language render (default mode)
  const render = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({
        email_type: emailType,
        country,
        customer_type: customerType,
      });
      if (overridesJson) params.set("overrides_json", overridesJson);
      const r = await axios.get(`${API}/admin/email-preview/render?${params}`);
      setHtml(r.data.html || "");
    } catch (e) {
      const raw = e.response?.data?.detail;
      const detail = typeof raw === "string"
        ? raw
        : Array.isArray(raw)
          ? raw.map(d => d?.msg || JSON.stringify(d)).join(" · ")
          : "Errore di rendering";
      setError(detail);
      setHtml("");
    } finally {
      setLoading(false);
    }
  }, [emailType, country, customerType, overridesJson]);

  // Multi-language render: 14 GDPR-native packs side-by-side for QA.
  const renderMulti = useCallback(async () => {
    setLoading(true);
    setError("");
    setMultiResults([]);
    try {
      const params = new URLSearchParams({
        email_type: emailType,
        customer_type: customerType,
      });
      const r = await axios.get(`${API}/admin/email-preview/render-multi?${params}`);
      setMultiResults(r.data.results || []);
    } catch (e) {
      const raw = e.response?.data?.detail;
      const detail = typeof raw === "string"
        ? raw
        : Array.isArray(raw)
          ? raw.map(d => d?.msg || JSON.stringify(d)).join(" · ")
          : "Errore di rendering";
      setError(detail);
      setMultiResults([]);
    } finally {
      setLoading(false);
    }
  }, [emailType, customerType]);

  useEffect(() => {
    if (options.email_types.length === 0) return;
    if (viewMode === "single") render();
    else renderMulti();
  }, [emailType, country, customerType, viewMode, options.email_types.length, overridesJson, render, renderMulti]);

  const customerTypeApplies = useMemo(() => emailType === "welcome", [emailType]);

  // Download the GDPR Translations Audit PDF (14 native packs, cover,
  // executive index, DPO countersignature). Triggered from the multi-
  // mode toolbar; useful for legal/DPO archiving.
  const [downloadingAudit, setDownloadingAudit] = useState(false);
  const downloadAudit = useCallback(async () => {
    setDownloadingAudit(true);
    try {
      const r = await axios.get(`${API}/admin/email-preview/gdpr-translations-audit.pdf`, {
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, "");
      a.download = `lojack-gdpr-translations-audit-${ts}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Audit PDF scaricato");
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error(`Download fallito: ${detail}`);
    } finally {
      setDownloadingAudit(false);
    }
  }, []);

  // Download the QA Approval User Guide PDF — to be emailed to Mara
  // and team before the QA review session.
  const [downloadingGuide, setDownloadingGuide] = useState(false);

  // Download the side-by-side translations PDF of the new (post-LoJack
  // Legal 04/06/2026) "Approvazione clausole specifiche" wording —
  // attached to the courtesy email so Legal can validate the 11
  // TelcaVoIP-produced translations alongside the 2 official ones.
  const [downloadingClauses, setDownloadingClauses] = useState(false);
  const downloadClausesAudit = useCallback(async () => {
    setDownloadingClauses(true);
    try {
      const r = await axios.get(
        `${API}/admin/email-preview/clauses-translations-audit.pdf`,
        { responseType: "blob", timeout: 30000 },
      );
      const ct = r.headers["content-type"] || "";
      if (!ct.includes("application/pdf")) {
        const text = await r.data.text();
        let detail = text;
        try { detail = JSON.parse(text).detail || text; } catch (_) { /* keep */ }
        throw new Error(`Server: ${ct} — ${String(detail).slice(0, 200)}`);
      }
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, "");
      a.download = `lojack-clauses-translations-${ts}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF confronto traduzioni clausole scaricato");
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error(`Download fallito: ${String(detail).slice(0, 200)}`);
      console.error("Clauses translations audit download failed:", e);
    } finally {
      setDownloadingClauses(false);
    }
  }, []);
  const downloadQaGuide = useCallback(async () => {
    setDownloadingGuide(true);
    try {
      const r = await axios.get(`${API}/admin/email-preview/qa-approval-user-guide.pdf`, {
        responseType: "blob",
        timeout: 90000, // 90s — generation includes a MongoDB live snapshot
      });
      // Guard: server may return 200 with an error JSON if something
      // partial went wrong. Check actual content-type before blobbing.
      const ct = r.headers["content-type"] || "";
      if (!ct.includes("application/pdf")) {
        // Try to surface the JSON detail
        const text = await r.data.text();
        let detail = text;
        try { detail = JSON.parse(text).detail || text; } catch (_) { /* fall back */ }
        throw new Error(`Server ha restituito ${ct || "tipo sconosciuto"}: ${String(detail).slice(0, 200)}`);
      }
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, "");
      a.download = `lojack-qa-approval-guide-${ts}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Guida QA PDF scaricata");
    } catch (e) {
      // axios timeout / network error / 5xx — extract the most useful message
      let detail = e.message || "errore sconosciuto";
      if (e.response) {
        // Server replied with non-2xx. Body is a Blob — read it as text.
        try {
          const blobText = await e.response.data.text();
          try { detail = JSON.parse(blobText).detail || blobText; } catch (_) { detail = blobText; }
        } catch (_) {
          detail = `HTTP ${e.response.status}`;
        }
      } else if (e.code === "ECONNABORTED") {
        detail = "Timeout (90s) — generazione PDF troppo lenta. Riprova o contatta IT.";
      }
      toast.error(`Download fallito: ${String(detail).slice(0, 200)}`);
      console.error("QA guide download failed:", e);
    } finally {
      setDownloadingGuide(false);
    }
  }, []);

  // ── Send TEST email (real SES delivery to logged-in admin) ──────
  // Same SES multipart pipeline production uses (CID inline images) so
  // the reviewer can validate the actual rendering inside Gmail /
  // Outlook / Apple Mail, not just the preview iframe.
  const [sendingTest, setSendingTest] = useState(false);
  const [lastTestSends, setLastTestSends] = useState([]);
  const [historyQaFilter, setHistoryQaFilter] = useState("all"); // all | pre | post
  const [pendingQa, setPendingQa] = useState({ count: 0, items: [] });

  const fetchTestHistory = useCallback(async () => {
    try {
      const r = await axios.get(
        `${API}/admin/email-preview/send-test-history?limit=5&qa_filter=${historyQaFilter}`,
      );
      setLastTestSends(r.data.items || []);
    } catch (_) { /* non-blocking */ }
  }, [historyQaFilter]);

  const fetchPendingQa = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/email-preview/pending-qa-tests`);
      setPendingQa(r.data);
    } catch (_) { /* non-blocking */ }
  }, []);

  useEffect(() => { fetchTestHistory(); }, [fetchTestHistory]);
  useEffect(() => { fetchPendingQa(); }, [fetchPendingQa, approvals.approved_count]);

  const sendTest = useCallback(async () => {
    const qaWarning = currentApproval?.approved
      ? ""
      : `\n\n⚠️ ATTENZIONE: la combinazione ${emailType} · ${country} NON è ancora ` +
        `approvata QA. Il test verrà registrato come "pre-approvazione" nell'audit.`;
    const ok = window.confirm(
      `Inviare un'email di test reale al tuo indirizzo?\n\n` +
      `Tipo: ${emailType}\nLingua/Paese: ${country}\n` +
      `${customerTypeApplies ? `Tipo cliente: ${customerType}\n` : ""}` +
      `\nL'email arriverà via SES con il pipeline produzione completo ` +
      `(CID inline images, multipart, signature). Limite: 10/ora.` +
      qaWarning
    );
    if (!ok) return;
    setSendingTest(true);
    try {
      const r = await axios.post(`${API}/admin/email-preview/send-test`, {
        email_type: emailType,
        country: country,
        customer_type: customerType,
      });
      toast.success(
        `Inviata a ${r.data.recipient}. ` +
        `Rate-limit residuo: ${r.data.rate_limit_remaining}/ora`,
        { duration: 6000 }
      );
      fetchTestHistory();
      fetchPendingQa();
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error(`Invio fallito: ${detail}`);
    } finally {
      setSendingTest(false);
    }
  }, [emailType, country, customerType, customerTypeApplies, fetchTestHistory, fetchPendingQa, currentApproval]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="email-preview-page">
      {/* Header */}
      <div className="border-b border-gray-800 bg-[#111]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-lojack-blue-500/15 text-lojack-blue-400 flex items-center justify-center">
              <MailOpen size={20} />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold">Email Preview Console</h1>
              <p className="text-gray-400 text-sm">
                Anteprima esatta delle email customer-facing nelle 31 lingue supportate. Nessuna email viene inviata.
              </p>
            </div>
            {/* Download QA Guide PDF (for Mara/Vercesi/Zorzutti) */}
            <a
              href="/admin/email-templates/overview"
              className="hidden md:inline-flex items-center gap-2 bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/40 text-emerald-300 rounded-xl px-3 py-2 text-xs font-semibold transition mr-2"
              data-testid="open-overview-page-btn"
              title="Panoramica personalizzazioni — vedi a colpo d'occhio dove Legal/Marketing ha modificato le email"
            >
              <Palette size={14} />
              Panoramica personalizzazioni
            </a>
            <button
              onClick={downloadQaGuide}
              disabled={downloadingGuide}
              className="hidden md:inline-flex items-center gap-2 bg-lojack-blue-600/15 hover:bg-lojack-blue-600/25 border border-lojack-blue-500/40 text-lojack-blue-200 disabled:opacity-50 rounded-xl px-3 py-2 text-xs font-semibold transition mr-2"
              data-testid="download-qa-guide-btn"
              title="Scarica la guida operativa PDF da inviare al team QA"
            >
              {downloadingGuide ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
              {downloadingGuide ? "Generazione…" : "Scarica guida QA"}
            </button>
            {/* Download Clauses translations audit PDF (for Legal validation) */}
            <button
              onClick={downloadClausesAudit}
              disabled={downloadingClauses}
              className="hidden md:inline-flex items-center gap-2 bg-lojack-clause-600/15 hover:bg-lojack-clause-600/25 border border-lojack-clause-500/40 text-lojack-clause-200 disabled:opacity-50 rounded-xl px-3 py-2 text-xs font-semibold transition mr-2"
              data-testid="download-clauses-audit-btn"
              title="Scarica il PDF di confronto traduzioni clausole (14 lingue) da allegare a Legal LoJack"
            >
              {downloadingClauses ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
              {downloadingClauses ? "Generazione…" : "PDF traduzioni clausole"}
            </button>
            {/* Global compliance progress bar */}
            <div
              className="hidden md:block bg-[#0a0a0a] border border-gray-800 rounded-xl px-4 py-2.5 min-w-[280px]"
              data-testid="email-approval-progress"
            >
              <div className="flex items-center justify-between text-[11px] uppercase tracking-wider mb-1.5">
                <span className="text-gray-500 font-semibold">Compliance approvazioni</span>
                <span className={`font-bold ${approvals.approval_pct >= 80 ? "text-emerald-400" : approvals.approval_pct >= 30 ? "text-lojack-clause-400" : "text-gray-400"}`}>
                  {approvals.approved_count}/{approvals.total_combinations} ({approvals.approval_pct}%)
                </span>
              </div>
              <div className="h-2 rounded-full bg-gray-800/60 overflow-hidden">
                <div
                  className={`h-full transition-all ${approvals.approval_pct >= 80 ? "bg-emerald-500" : approvals.approval_pct >= 30 ? "bg-lojack-clause-400" : "bg-gray-500"}`}
                  style={{ width: `${Math.max(1, approvals.approval_pct)}%` }}
                />
              </div>
            </div>
          </div>
          {/* View mode toggle — single vs side-by-side */}
          <div className="flex gap-2 mt-4" data-testid="view-mode-toggle">
            <button
              onClick={() => setViewMode("single")}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition border ${
                viewMode === "single"
                  ? "bg-lojack-blue-600/15 border-lojack-blue-500 text-lojack-blue-300"
                  : "bg-[#0a0a0a] border-gray-700 text-gray-500 hover:text-white"
              }`}
              data-testid="view-mode-single"
            >
              <Square size={13} /> Singola lingua
            </button>
            <button
              onClick={() => setViewMode("multi")}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition border ${
                viewMode === "multi"
                  ? "bg-lojack-blue-600/15 border-lojack-blue-500 text-lojack-blue-300"
                  : "bg-[#0a0a0a] border-gray-700 text-gray-500 hover:text-white"
              }`}
              data-testid="view-mode-multi"
              title="Side-by-side: 14 lingue native GDPR a confronto"
            >
              <Grid3x3 size={13} /> Side-by-side (14 lingue)
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
        {/* Controls */}
        <div className="space-y-4">
          <div className="bg-[#111] border border-gray-800 rounded-2xl p-5">
            <h3 className="text-xs uppercase tracking-wider text-gray-500 mb-3 font-semibold">Tipo email</h3>
            <select
              value={emailType}
              onChange={e => setEmailType(e.target.value)}
              className="w-full bg-[#0a0a0a] border border-gray-700 rounded-xl px-3 py-2.5 text-white focus:border-lojack-blue-500 outline-none"
              data-testid="email-type-select"
            >
              {/* Group options by ``group`` field. Preserves the order
                  returned by the backend so QA can rely on a stable
                  scrolling sequence. */}
              {(() => {
                const groups = [];
                const seen = new Set();
                for (const t of options.email_types) {
                  const g = t.group || "Altro";
                  if (!seen.has(g)) { seen.add(g); groups.push(g); }
                }
                return groups.map((g) => (
                  <optgroup key={g} label={g}>
                    {options.email_types.filter(t => (t.group || "Altro") === g).map(t => (
                      <option key={t.id} value={t.id}>{t.label}</option>
                    ))}
                  </optgroup>
                ));
              })()}
            </select>
          </div>

          {viewMode === "single" && (
            <div className="bg-[#111] border border-gray-800 rounded-2xl p-5" data-testid="country-select-card">
              <h3 className="text-xs uppercase tracking-wider text-gray-500 mb-3 font-semibold">Paese del destinatario</h3>
              <select
                value={country}
                onChange={e => setCountry(e.target.value)}
                className="w-full bg-[#0a0a0a] border border-gray-700 rounded-xl px-3 py-2.5 text-white focus:border-lojack-blue-500 outline-none"
                data-testid="email-country-select"
              >
                {options.countries.map(c => (
                  <option key={c} value={c}>{FLAGS[c] || "🌍"} {c}</option>
                ))}
              </select>
              <p className="text-gray-500 text-[11px] mt-2 leading-relaxed">
                Il paese determina la lingua del corpo email e del footer GDPR (Art. 12 RGPD).
              </p>
            </div>
          )}

          {viewMode === "multi" && (
            <div className="bg-[#111] border border-gray-800 rounded-2xl p-5" data-testid="multi-mode-info">
              <h3 className="text-xs uppercase tracking-wider text-gray-500 mb-3 font-semibold">Modalità revisione</h3>
              <p className="text-gray-300 text-xs leading-relaxed">
                Renderizza le <strong className="text-white">14 lingue native</strong> in griglia per validazione madrelingua e QA traduzioni.
              </p>
              <p className="text-gray-500 text-[11px] mt-2 leading-relaxed">
                IT, UK, FR, ES, DE, PL, PT, NL, SE, NO, FI, DK, CZ, GR.<br />
                I paesi CH, BE, GBZ, IRL sono coperti via alias (DE/FR/UK) e non appaiono.
              </p>
              <button
                onClick={downloadAudit}
                disabled={downloadingAudit}
                className="mt-4 w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold border border-lojack-blue-500/40 bg-lojack-blue-600/15 text-lojack-blue-300 hover:bg-lojack-blue-600/25 disabled:opacity-50 transition"
                data-testid="download-audit-pdf-btn"
                title="Scarica il dossier PDF con tutti i 14 pacchetti GDPR (per controfirma DPO)"
              >
                {downloadingAudit ? <Loader2 size={13} className="animate-spin" /> : <FileText size={13} />}
                {downloadingAudit ? "Generazione…" : "Scarica audit PDF"}
              </button>
              <p className="text-gray-500 text-[10px] mt-2 leading-relaxed italic">
                Dossier signabile dal DPO con cover, index, 14 pacchetti completi e fingerprint del catalogo runtime.
              </p>
            </div>
          )}

          {customerTypeApplies && (
            <div className="bg-[#111] border border-gray-800 rounded-2xl p-5" data-testid="customer-type-card">
              <h3 className="text-xs uppercase tracking-wider text-gray-500 mb-3 font-semibold">Tipo cliente</h3>
              <div className="flex gap-2">
                <button
                  onClick={() => setCustomerType("private")}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition ${customerType === "private" ? "bg-lojack-blue-600 text-white" : "bg-[#0a0a0a] border border-gray-700 text-gray-400 hover:text-white"}`}
                  data-testid="customer-type-private"
                >
                  Privato
                </button>
                <button
                  onClick={() => setCustomerType("business")}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition ${customerType === "business" ? "bg-lojack-blue-600 text-white" : "bg-[#0a0a0a] border border-gray-700 text-gray-400 hover:text-white"}`}
                  data-testid="customer-type-business"
                >
                  Azienda
                </button>
              </div>
            </div>
          )}

          {/* Edit panel (Mara CR 17/06/2026 P2) — override fixture
              values to preview the email with the actual customer
              being onboarded. Only the GET /render path uses these,
              so multi-mode keeps the synthetic defaults. */}
          {viewMode === "single" && (
            <div className="bg-[#111] border border-gray-800 rounded-2xl p-5" data-testid="edit-fixture-card">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs uppercase tracking-wider text-gray-500 font-semibold flex items-center gap-2">
                  <Pencil size={12} />
                  Personalizza dati anteprima
                </h3>
                <button
                  onClick={() => setEditOpen(o => !o)}
                  className="text-xs text-lojack-blue-400 hover:text-lojack-blue-300 transition"
                  data-testid="edit-fixture-toggle"
                >
                  {editOpen ? "Nascondi" : "Modifica"}
                </button>
              </div>
              {!editOpen && !overridesActive && (
                <p className="text-gray-500 text-[11px] leading-relaxed">
                  L&apos;anteprima usa il cliente fittizio standard (Maria Bianchi, Fiat 500X).
                  Clicca <em>Modifica</em> per sostituire nome, veicolo, telefono ecc.
                </p>
              )}
              {!editOpen && overridesActive && (
                <div className="flex items-center justify-between text-xs">
                  <span className="text-emerald-400 flex items-center gap-1" data-testid="edit-fixture-active-pill">
                    <Check size={12} />
                    Override attivo ({Object.values(fixtureOverrides).filter(v => (v ?? '').trim()).length} campi)
                  </span>
                  <button
                    onClick={resetOverrides}
                    className="text-gray-400 hover:text-white"
                    data-testid="edit-fixture-reset-mini"
                    title="Annulla tutti gli override"
                  >
                    <RotateCcw size={12} />
                  </button>
                </div>
              )}
              {editOpen && (
                <div className="space-y-2.5 mt-2">
                  {FIXTURE_FIELDS.map(({ key, label, placeholder }) => (
                    <div key={key}>
                      <label className="text-gray-500 text-[10px] uppercase tracking-wide">{label}</label>
                      <input
                        value={fixtureOverrides[key] ?? ""}
                        onChange={e => setFixtureOverrides(o => ({ ...o, [key]: e.target.value }))}
                        placeholder={placeholder}
                        className="w-full mt-1 bg-[#0a0a0a] border border-gray-700 rounded-lg px-2.5 py-1.5 text-sm text-white focus:border-lojack-blue-500 outline-none"
                        data-testid={`edit-fixture-input-${key}`}
                      />
                    </div>
                  ))}
                  <div className="flex items-center justify-between pt-2 gap-2">
                    <button
                      onClick={resetOverrides}
                      disabled={!overridesActive}
                      className="flex-1 text-xs px-3 py-1.5 rounded-lg border border-gray-700 text-gray-300 hover:bg-gray-800 disabled:opacity-40 transition flex items-center justify-center gap-1.5"
                      data-testid="edit-fixture-reset"
                    >
                      <RotateCcw size={11} />
                      Reset
                    </button>
                  </div>
                  <p className="text-gray-500 text-[10px] leading-relaxed italic pt-1">
                    Solo i campi compilati sovrascrivono il default. Lasciare vuoto per mantenere il valore standard.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Editor testi email (req. Mara 09/07/2026) — visibile solo
              per i 3 tipi editabili; il salvataggio rigenera l'anteprima. */}
          {viewMode === "single" && (
            <EmailTemplateEditor emailType={emailType} country={country} onSaved={render} />
          )}

          <button
            onClick={() => (viewMode === "single" ? render() : renderMulti())}
            disabled={loading}
            className="w-full bg-lojack-blue-600 hover:bg-lojack-blue-700 disabled:opacity-50 rounded-xl px-4 py-2.5 font-semibold flex items-center justify-center gap-2 transition"
            data-testid="email-refresh-btn"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
            Rigenera anteprima
          </button>

          {/* Real-send test (Gmail/Outlook/Apple Mail validation) */}
          <button
            onClick={sendTest}
            disabled={sendingTest || loading}
            className="w-full bg-lojack-clause-600/20 hover:bg-lojack-clause-600/30 border border-lojack-clause-500/40 text-lojack-clause-200 disabled:opacity-50 rounded-xl px-4 py-2.5 font-semibold flex items-center justify-center gap-2 transition"
            data-testid="email-send-test-btn"
            title="Invia un'email reale a te stesso via SES per testare il rendering nel tuo client"
          >
            {sendingTest ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            {sendingTest ? "Invio in corso…" : "Invia mail di test a me"}
          </button>
          {!currentApproval?.approved && (
            <div
              className="flex items-start gap-2 bg-lojack-clause-500/10 border border-lojack-clause-500/30 rounded-lg px-2.5 py-2 -mt-1"
              data-testid="email-send-test-qa-warning"
            >
              <AlertCircle size={12} className="text-lojack-clause-300 shrink-0 mt-0.5" />
              <p className="text-lojack-clause-200/90 text-[10px] leading-relaxed">
                Stai per inviare un test su una combinazione <strong>NON ancora approvata QA</strong>.
                Il test sarà registrato come "pre-approvazione" nell'audit log.
              </p>
            </div>
          )}
          <p className="text-lojack-clause-200/60 text-[10px] -mt-1 leading-relaxed">
            Spedisce l'email REALE via SES al tuo indirizzo admin con CID inline images.
            Utile per verificare il rendering in Gmail / Outlook / Apple Mail. Max 10/ora.
          </p>

          {/* QA Approval card (Mara/Vercesi/Zorzutti compliance) */}
          <div
            className={`border rounded-2xl p-4 space-y-3 ${currentApproval?.approved ? "bg-emerald-500/5 border-emerald-500/30" : "bg-[#0a0a0a] border-gray-800/60"}`}
            data-testid="email-approval-card"
          >
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold">
              {currentApproval?.approved ? (
                <CheckCircle2 size={14} className="text-emerald-400" />
              ) : (
                <Circle size={14} className="text-gray-500" />
              )}
              <span className={currentApproval?.approved ? "text-emerald-300" : "text-gray-400"}>
                Approvazione QA · {emailType} · {country}
              </span>
            </div>
            <textarea
              value={approvalNotes}
              onChange={(e) => setApprovalNotes(e.target.value)}
              placeholder="Note QA (opz.): es. 'Logo non centrato in Outlook 2019, gestire fallback'"
              rows={2}
              maxLength={500}
              className="w-full bg-[#0a0a0a] border border-gray-700 rounded-lg px-2.5 py-2 text-xs text-gray-200 focus:border-lojack-blue-500 outline-none resize-none"
              data-testid="email-approval-notes"
            />
            <button
              onClick={() => toggleApproval(!currentApproval?.approved)}
              className={`w-full px-3 py-2 rounded-lg text-xs font-semibold transition flex items-center justify-center gap-2 ${
                currentApproval?.approved
                  ? "bg-red-500/15 border border-red-500/30 text-red-300 hover:bg-red-500/25"
                  : "bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/25"
              }`}
              data-testid="email-approval-toggle-btn"
            >
              <Check size={13} />
              {currentApproval?.approved
                ? "Revoca approvazione"
                : "Approva questa combinazione"}
            </button>
            <button
              onClick={bulkApproveForCountry}
              disabled={bulkLoading}
              title={`Marca come approvate tutte le ${options?.email_types?.length || 22} email per ${country} (richiede conferma)`}
              className="w-full px-3 py-2 rounded-lg text-[11px] font-semibold transition flex items-center justify-center gap-2 bg-lojack-blue-500/10 border border-lojack-blue-500/30 text-lojack-blue-200 hover:bg-lojack-blue-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="email-approval-bulk-by-country-btn"
            >
              {bulkLoading ? <Loader2 size={12} className="animate-spin" /> : <CheckCheck size={12} />}
              {bulkLoading
                ? `Approvazione in corso...`
                : `Approva tutte le email per ${country} (${options?.email_types?.length || 22})`}
            </button>
            {currentApproval?.approved && (
              <p className="text-[10px] text-emerald-400/70 leading-relaxed">
                ✓ Approvato da <strong>{currentApproval.approved_by}</strong>
                {" il "}{new Date(currentApproval.approved_at).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
              </p>
            )}
          </div>

          {/* Pending pre-QA combinations widget */}
          {pendingQa.count > 0 && (
            <div
              className="bg-lojack-clause-500/5 border border-lojack-clause-500/30 rounded-2xl p-4 space-y-2"
              data-testid="pending-qa-widget"
            >
              <div className="flex items-center justify-between text-xs uppercase tracking-wider font-semibold">
                <span className="text-lojack-clause-300 flex items-center gap-2">
                  <AlertCircle size={12} /> Test pre-QA non chiusi
                </span>
                <span className="text-lojack-clause-400 font-bold" data-testid="pending-qa-count">
                  {pendingQa.count}
                </span>
              </div>
              <ul className="space-y-1 text-[11px] text-lojack-clause-100/80 max-h-32 overflow-y-auto">
                {pendingQa.items.slice(0, 6).map((it, idx) => (
                  <li
                    key={`${it.email_type}-${it.country}`}
                    className="flex items-center justify-between gap-2 hover:bg-lojack-clause-500/10 px-1.5 py-0.5 rounded cursor-pointer"
                    onClick={() => { setEmailType(it.email_type); setCountry(it.country); }}
                    data-testid={`pending-qa-row-${idx}`}
                  >
                    <span className="truncate flex items-center gap-1.5">
                      <span className="text-base">{FLAGS[it.country] || "🌍"}</span>
                      <strong className="text-lojack-clause-200">{it.email_type}</strong>
                      <span className="text-lojack-clause-400/70">×{it.test_count}</span>
                    </span>
                  </li>
                ))}
                {pendingQa.count > 6 && (
                  <li className="text-lojack-clause-300/60 text-[10px] italic px-1.5">
                    + altri {pendingQa.count - 6} ancora aperti…
                  </li>
                )}
              </ul>
              <p className="text-[10px] text-lojack-clause-200/60 leading-relaxed">
                Combinazioni testate ma non ancora chiuse da approvazione QA. Clicca per saltare.
              </p>
            </div>
          )}

          {(lastTestSends.length > 0 || historyQaFilter !== "all") && (
            <div className="bg-[#0a0a0a] border border-gray-800/60 rounded-2xl p-4 space-y-2" data-testid="email-test-history">
              <div className="flex items-center justify-between gap-2 text-xs uppercase tracking-wider text-gray-500 font-semibold">
                <span className="flex items-center gap-2"><Clock size={12} /> Cronologia invii test</span>
              </div>
              {/* QA filter pills */}
              <div className="flex gap-1.5" data-testid="email-test-history-qa-filter">
                {[
                  { id: "all", label: "Tutti", cls: "text-gray-300 bg-gray-700/40 border-gray-600/40" },
                  { id: "post", label: "✓ QA", cls: "text-emerald-300 bg-emerald-500/15 border-emerald-500/30" },
                  { id: "pre", label: "⏳ pre-QA", cls: "text-lojack-clause-300 bg-lojack-clause-500/10 border-lojack-clause-500/30" },
                ].map(opt => (
                  <button
                    key={opt.id}
                    onClick={() => setHistoryQaFilter(opt.id)}
                    className={`px-2 py-0.5 rounded text-[10px] font-semibold border transition ${
                      historyQaFilter === opt.id ? opt.cls : "text-gray-600 bg-transparent border-gray-800 hover:text-gray-400"
                    }`}
                    data-testid={`email-test-history-qa-filter-${opt.id}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <ul className="space-y-1.5 text-[11px] text-gray-400">
                {lastTestSends.length === 0 && (
                  <li className="text-gray-600 italic text-[10px] py-1" data-testid="email-test-history-empty">
                    Nessun test con questo filtro.
                  </li>
                )}
                {lastTestSends.map((it, idx) => (
                  <li key={idx} className="flex items-center justify-between gap-2" data-testid={`email-test-history-row-${idx}`}>
                    <span className="truncate flex items-center gap-1.5">
                      <span className="text-base">{FLAGS[it.country] || "🌍"}</span>
                      <strong className="text-gray-300">{it.email_type}</strong>
                      {it.qa_approved_at_send ? (
                        <span
                          title="Test inviato dopo l'approvazione QA"
                          className="inline-flex items-center text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 font-semibold"
                          data-testid={`email-test-history-row-${idx}-qa-badge`}
                        >
                          ✓ QA
                        </span>
                      ) : (
                        <span
                          title="Test inviato PRIMA dell'approvazione QA"
                          className="inline-flex items-center text-[9px] px-1.5 py-0.5 rounded bg-lojack-clause-500/10 border border-lojack-clause-500/30 text-lojack-clause-300 font-semibold"
                          data-testid={`email-test-history-row-${idx}-qa-badge`}
                        >
                          ⏳ pre-QA
                        </span>
                      )}
                    </span>
                    <span className="text-gray-600 font-mono shrink-0">
                      {new Date(it.created_at).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="bg-[#0a0a0a] border border-gray-800/60 rounded-2xl p-4 text-[11px] text-gray-500 leading-relaxed">
            <strong className="text-gray-400 block mb-1">Dati di fixture</strong>
            Maria Bianchi (privato) o Acme S.p.A. (azienda) — Fiat 500X, targa AB123CD.
            Nessuna interazione con la collezione clienti reale.
          </div>
        </div>

        {/* Preview area */}
        {viewMode === "single" ? (
          <div className="bg-[#111] border border-gray-800 rounded-2xl overflow-hidden min-h-[600px]" data-testid="email-preview-frame-wrap">
            {loading && (
              <div className="flex items-center justify-center h-[600px] text-gray-500 gap-2" data-testid="email-preview-loading">
                <Loader2 size={20} className="animate-spin" />
                Generazione…
              </div>
            )}
            {!loading && error && (
              <div className="flex items-center justify-center h-[600px] text-red-400 gap-3 p-6" data-testid="email-preview-error">
                <AlertCircle size={20} />
                <div>
                  <div className="font-semibold">Errore di rendering</div>
                  <div className="text-xs text-red-300/80 mt-1">{error}</div>
                </div>
              </div>
            )}
            {!loading && !error && html && (
              <iframe
                title="Email preview"
                srcDoc={html}
                sandbox="allow-same-origin"
                className="w-full min-h-[850px] bg-[#0a0a0a]"
                data-testid="email-preview-iframe"
              />
            )}
          </div>
        ) : (
          <div data-testid="email-preview-multi-grid">
            {loading && (
              <div className="flex items-center justify-center h-[400px] text-gray-500 gap-2 bg-[#111] border border-gray-800 rounded-2xl">
                <Loader2 size={20} className="animate-spin" />
                Rendering 14 lingue…
              </div>
            )}
            {!loading && error && (
              <div className="flex items-center justify-center h-[400px] text-red-400 gap-3 p-6 bg-[#111] border border-gray-800 rounded-2xl" data-testid="email-preview-multi-error">
                <AlertCircle size={20} />
                <div>
                  <div className="font-semibold">Errore di rendering</div>
                  <div className="text-xs text-red-300/80 mt-1">{error}</div>
                </div>
              </div>
            )}
            {!loading && !error && multiResults.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {multiResults.map(r => (
                  <div
                    key={r.country}
                    className="bg-[#111] border border-gray-800 rounded-xl overflow-hidden flex flex-col"
                    data-testid={`email-preview-multi-card-${r.country}`}
                  >
                    <div className="flex items-center justify-between px-3 py-2 border-b border-gray-800 bg-[#0f0f0f]">
                      <span className="text-sm font-semibold flex items-center gap-2">
                        <span className="text-base">{FLAGS[r.country] || "🌍"}</span>
                        <span>{r.country}</span>
                      </span>
                      {r.ok ? (
                        <span className="text-[10px] uppercase tracking-wider text-lojack-blue-400/80 font-mono">OK</span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-wider text-red-400 font-mono" title={r.error || ""}>ERR</span>
                      )}
                    </div>
                    {r.ok ? (
                      // Side-by-side cards are ~380px wide while production
                      // emails are designed for 640px. We CSS-scale the
                      // iframe down so the whole email fits at-a-glance —
                      // the QA reviewer can still scroll inside an
                      // individual preview when needed.
                      <div
                        className="relative w-full bg-[#0a0a0a] overflow-hidden"
                        style={{ height: 520 }}
                      >
                        <iframe
                          title={`Preview ${r.country}`}
                          srcDoc={r.html}
                          sandbox="allow-same-origin"
                          className="bg-[#0a0a0a]"
                          style={{
                            // Scale 65% so the full 640px-wide email
                            // becomes ~416px wide and slots inside the
                            // 380px card with a tiny horizontal bleed.
                            transform: "scale(0.62)",
                            transformOrigin: "top left",
                            width: "161.3%",
                            height: "161.3%",
                            border: 0,
                          }}
                          data-testid={`email-preview-multi-iframe-${r.country}`}
                        />
                      </div>
                    ) : (
                      <div className="p-4 text-xs text-red-300/80 leading-relaxed bg-[#0a0a0a]" style={{ height: 520 }}>
                        {r.error || "Render failed"}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailPreviewPage;
